import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { UpdateUserResponse, userApi } from "@/services/userService";
import { Home, User, Lock, LogOut, Bell, BellOff } from "lucide-react";
import { PasswordInput } from "@/components/ui/password-input";
import { passwordSchema } from "@/lib/passwordValidation";
import { useDeviceType } from "@/hooks/use-mobile";
import { MobileContainer, MobileSpacing } from "@/components/mobile/ResponsiveLayout";
import MobileCard from "@/components/mobile/MobileCard";
import { usePushNotifications } from "@/hooks/usePushNotifications";

// Form schemas
const profileFormSchema = z.object({
  firstName: z.string().min(2, { message: "First name must be at least 2 characters." }),
  lastName: z.string().min(2, { message: "Last name must be at least 2 characters." }),
  company: z.string().optional(),
});

const passwordFormSchema = z
  .object({
    currentPassword: passwordSchema,
    newPassword: passwordSchema,
    confirmPassword: passwordSchema,
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type ProfileFormValues = z.infer<typeof profileFormSchema>;
type PasswordFormValues = z.infer<typeof passwordFormSchema>;

const Settings = () => {
  const { user, setUser, logout } = useAuth();
  const { toast } = useToast();
  const [profileError, setProfileError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const { isMobile } = useDeviceType();
  const [activeTab, setActiveTab] = useState("profile");
  const { isSupported, isSubscribed, isLoading, subscribe, unsubscribe } = usePushNotifications();

  // Profile form
  const profileForm = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      company: user?.companyName || "",
    },
  });

  // Password form
  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Update profile when user data loads
  useEffect(() => {
    if (user) {
      profileForm.reset({
        firstName: user.firstName,
        lastName: user.lastName,
        company: user.companyName || "",
      });
    }
  }, [user, profileForm]);

  // Profile update mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: ProfileFormValues) => {
      if (!user) throw new Error("User not authenticated");
      return userApi.updateUser(user._id, {
        firstName: data.firstName,
        lastName: data.lastName,
        companyName: data.company || "",
      });
    },
    onSuccess: (data: UpdateUserResponse) => {
      setProfileError(null);
      setUser(data.user);
      localStorage.setItem("currentUser", JSON.stringify(data.user));
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      setProfileError(error.message);
      toast({
        title: "Error",
        description: "There was a problem updating your profile.",
        variant: "destructive",
      });
    },
  });

  // Password update mutation
  const updatePasswordMutation = useMutation({
    mutationFn: (data: PasswordFormValues) => {
      if (!user) throw new Error("User not authenticated");
      return userApi.updateUserPassword(user._id, {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
    },
    onSuccess: () => {
      setPasswordError(null);
      passwordForm.reset();
      toast({
        title: "Password updated",
        description: "Your password has been changed successfully.",
      });
    },
    onError: (error: Error) => {
      setPasswordError(error.message);
      toast({
        title: "Error",
        description: "There was a problem updating your password.",
        variant: "destructive",
      });
    },
  });

  // Form submission handlers
  const onProfileSubmit = (data: ProfileFormValues) =>
    updateProfileMutation.mutate(data);
  const onPasswordSubmit = (data: PasswordFormValues) =>
    updatePasswordMutation.mutate(data);

  if (!user) {
    return (
      <div className="p-4">
        <Alert>
          <AlertDescription>
            You need to be logged in to access this page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isMobile) {
    return (
      <MobileContainer>
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <nav className="flex items-center text-sm text-muted-foreground mb-1" aria-label="Breadcrumb">
                <Link to="/" className="hover:underline text-primary font-medium flex items-center">
                  <Home className="h-4 w-4 mr-1" />
                </Link>
                <span className="mx-2">/</span>
                <span className="text-foreground font-semibold">Settings</span>
              </nav>
              <h1 className="text-2xl font-bold">Settings</h1>
            </div>
          </div>

          <MobileSpacing size="sm" />

          {/* Profile Section */}
          <MobileCard 
            title="Profile Information"
            subtitle="Update your personal information"
            header={
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Profile Information</h3>
                  <p className="text-sm text-muted-foreground">Update your personal information</p>
                </div>
              </div>
            }
          >
            {profileError && (
              <Alert className="mb-4">
                <AlertDescription>{profileError}</AlertDescription>
              </Alert>
            )}
            <Form {...profileForm}>
              <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-4">
                <div className="grid gap-4">
                  <FormField
                    control={profileForm.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter your first name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={profileForm.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter your last name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={profileForm.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter your company name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                  <Button 
                  type="submit" 
                  className="w-full btn-gradient shadow-sophisticated" 
                  disabled={updateProfileMutation.isPending}
                >
                  {updateProfileMutation.isPending ? "Updating..." : "Update Profile"}
                </Button>
              </form>
            </Form>
          </MobileCard>

          {/* Password Section */}
          <MobileCard 
            header={
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Lock className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Change Password</h3>
                  <p className="text-sm text-muted-foreground">Update your account password</p>
                </div>
              </div>
            }
          >
            {passwordError && (
              <Alert className="mb-4">
                <AlertDescription>{passwordError}</AlertDescription>
              </Alert>
            )}
            <Form {...passwordForm}>
              <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4">
                <FormField
                  control={passwordForm.control}
                  name="currentPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Current Password</FormLabel>
                      <FormControl>
                        <PasswordInput placeholder="Enter current password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={passwordForm.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Password</FormLabel>
                      <FormControl>
                        <PasswordInput placeholder="Enter new password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={passwordForm.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm New Password</FormLabel>
                      <FormControl>
                        <PasswordInput placeholder="Confirm new password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button 
                  type="submit" 
                  className="w-full btn-gradient shadow-sophisticated" 
                  disabled={updatePasswordMutation.isPending}
                >
                  {updatePasswordMutation.isPending ? "Updating..." : "Change Password"}
                </Button>
              </form>
            </Form>
          </MobileCard>

          {/* Push Notifications Section */}
          <MobileCard 
            title="Push Notifications"
            subtitle="Manage your notification preferences"
            header={
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Bell className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Push Notifications</h3>
                  <p className="text-sm text-muted-foreground">Receive updates even when the app is closed</p>
                </div>
              </div>
            }
          >
            {!isSupported ? (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground">
                  Push notifications are not supported in this browser
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="font-medium">
                    {isSubscribed ? "Enabled" : "Disabled"}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {isSubscribed 
                      ? "You're receiving push notifications" 
                      : "Enable to receive notifications when app is closed"}
                  </div>
                </div>
                <Button
                  onClick={isSubscribed ? unsubscribe : subscribe}
                  disabled={isLoading}
                  variant={isSubscribed ? "outline" : "default"}
                  size="sm"
                  className={!isSubscribed ? "btn-gradient shadow-sophisticated" : ""}
                >
                  {isLoading ? (
                    "Loading..."
                  ) : isSubscribed ? (
                    <>
                      <BellOff className="h-4 w-4 mr-2" />
                      Disable
                    </>
                  ) : (
                    <>
                      <Bell className="h-4 w-4 mr-2" />
                      Enable
                    </>
                  )}
                </Button>
              </div>
            )}
          </MobileCard>

          {/* Logout Section */}
          <MobileCard className="border-destructive/20">
            <div className="text-center py-4">
              <LogOut className="h-8 w-8 mx-auto mb-3 text-destructive" />
              <h3 className="font-medium mb-2">Sign Out</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Sign out of your account on this device
              </p>
              <Button 
                variant="destructive" 
                onClick={logout}
                className="w-full"
              >
                Sign Out
              </Button>
            </div>
          </MobileCard>

          {/* Bottom spacing for navigation */}
          <div className="h-20" />
        </div>
      </MobileContainer>
    );
  }

  // Desktop layout (original)
  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <nav
          className="flex items-center text-sm text-muted-foreground mb-2"
          aria-label="Breadcrumb"
        >
          <Link
            to="/"
            className="hover:underline text-primary font-medium flex items-center"
          >
            <Home className="h-4 w-4 mr-1" />
          </Link>
          <span className="mx-2">/</span>
          <span className="text-foreground font-semibold">
            Account Settings
          </span>
        </nav>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <div className="sticky top-0 z-20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 pb-4 -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="w-full justify-start h-16 p-2 bg-slate-100/80 dark:bg-muted/40 rounded-2xl border shadow-inner gap-2">
            <TabsTrigger 
              value="profile"
              className="flex-1 md:flex-none h-full data-[state=active]:bg-white dark:data-[state=active]:bg-background data-[state=active]:shadow-lg data-[state=active]:ring-1 data-[state=active]:ring-black/5 data-[state=active]:text-foreground transition-all duration-300 rounded-xl font-medium text-muted-foreground"
            >
              <User className="h-4 w-4 mr-2.5" />
              Profile
            </TabsTrigger>
            <TabsTrigger 
              value="password"
              className="flex-1 md:flex-none h-full data-[state=active]:bg-white dark:data-[state=active]:bg-background data-[state=active]:shadow-lg data-[state=active]:ring-1 data-[state=active]:ring-black/5 data-[state=active]:text-foreground transition-all duration-300 rounded-xl font-medium text-muted-foreground"
            >
              <Lock className="h-4 w-4 mr-2.5" />
              Password
            </TabsTrigger>
            <TabsTrigger 
              value="notifications"
              className="flex-1 md:flex-none h-full data-[state=active]:bg-white dark:data-[state=active]:bg-background data-[state=active]:shadow-lg data-[state=active]:ring-1 data-[state=active]:ring-black/5 data-[state=active]:text-foreground transition-all duration-300 rounded-xl font-medium text-muted-foreground"
            >
              <Bell className="h-4 w-4 mr-2.5" />
              Notifications
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle>Profile Information</CardTitle>
                  <CardDescription>
                    Update your personal information and company details.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {profileError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{profileError}</AlertDescription>
                </Alert>
              )}

              <Form {...profileForm}>
                <form
                  onSubmit={profileForm.handleSubmit(onProfileSubmit)}
                  className="space-y-4"
                >
                  <FormField
                    control={profileForm.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Your first name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={profileForm.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Your last name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={profileForm.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Your company (optional)"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormDescription>
                          This is the company that will be displayed on your
                          profile.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator className="my-4" />

                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      className="btn-gradient shadow-sophisticated"
                      disabled={updateProfileMutation.isPending}
                    >
                      {updateProfileMutation.isPending
                        ? "Saving..."
                        : "Save Changes"}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle>Account Information</CardTitle>
                  <CardDescription>View your account details.</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="p-4 rounded-xl bg-muted/30 border border-border/50 shadow-inner">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Email Address</div>
                  <div className="text-sm font-medium text-foreground">
                    {user.email}
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-muted/30 border border-border/50 shadow-inner">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Account Role</div>
                  <div className="text-sm font-medium text-foreground capitalize">
                    {user.role}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="password" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Lock className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle>Change Password</CardTitle>
                  <CardDescription>
                    Update your password to keep your account secure.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {passwordError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{passwordError}</AlertDescription>
                </Alert>
              )}

              <Form {...passwordForm}>
                <form
                  onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
                  className="space-y-4"
                >
                  <FormField
                    control={passwordForm.control}
                    name="currentPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Current Password</FormLabel>
                        <FormControl>
                          <PasswordInput
                            placeholder="Enter your current password"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={passwordForm.control}
                    name="newPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>New Password</FormLabel>
                        <FormControl>
                          <PasswordInput
                            placeholder="Enter your new password"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={passwordForm.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirm New Password</FormLabel>
                        <FormControl>
                          <PasswordInput
                            placeholder="Confirm your new password"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator className="my-4" />

                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      className="btn-gradient shadow-sophisticated"
                      disabled={updatePasswordMutation.isPending}
                    >
                      {updatePasswordMutation.isPending
                        ? "Updating..."
                        : "Update Password"}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Bell className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle>Push Notifications</CardTitle>
                  <CardDescription>
                    Manage your push notification preferences for project updates and activities.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!isSupported ? (
                <Alert>
                  <AlertDescription>
                    Push notifications are not supported in this browser. Please use a modern browser like Chrome, Firefox, Edge, or Safari (iOS 16.4+).
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-1">
                      <div className="font-medium flex items-center gap-2">
                        {isSubscribed ? (
                          <>
                            <Bell className="h-4 w-4 text-green-600" />
                            Push Notifications Enabled
                          </>
                        ) : (
                          <>
                            <BellOff className="h-4 w-4 text-muted-foreground" />
                            Push Notifications Disabled
                          </>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {isSubscribed 
                          ? "You're receiving push notifications for project updates, comments, and status changes" 
                          : "Enable push notifications to receive real-time updates even when the app is closed"}
                      </div>
                    </div>
                    <Button
                      onClick={isSubscribed ? unsubscribe : subscribe}
                      disabled={isLoading}
                      variant={isSubscribed ? "outline" : "default"}
                      className={!isSubscribed ? "btn-gradient shadow-sophisticated" : ""}
                    >
                      {isLoading ? (
                        "Loading..."
                      ) : isSubscribed ? (
                        <>
                          <BellOff className="h-4 w-4 mr-2" />
                          Disable
                        </>
                      ) : (
                        <>
                          <Bell className="h-4 w-4 mr-2" />
                          Enable
                        </>
                      )}
                    </Button>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <h4 className="font-medium text-sm">You'll receive notifications for:</h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="text-primary">•</span>
                        <span>New projects created by clients</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary">•</span>
                        <span>Project status changes and updates</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary">•</span>
                        <span>New comments and replies on your projects</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary">•</span>
                        <span>When you're mentioned in comments</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary">•</span>
                        <span>Submission updates and document uploads</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary">•</span>
                        <span>Team member activity and assignments</span>
                      </li>
                    </ul>
                  </div>

                  <Alert>
                    <AlertDescription className="text-sm">
                      <strong>Note:</strong> Push notifications work even when the app is not open. Make sure your browser allows notifications from this site.
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
